import * as React from 'react';
import { DataSignal } from 's-js';
import * as cx from 'classnames';
import SuperCritical from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';
import { ManagePilotLookups, AgencyCodeViewModel, PilotViewModel, LineHandlingCompanyViewModel, TugCompanyViewModel } from './model';

import { ValidationErrorView } from 'core/validationerror/view';

export const ManagePilotLookupsView = SuperCritical(({ model: m } : { model : ManagePilotLookups }) =>
    <div className="managepilotlookups pc-page-fixedwidth">
        {/*<loadingindicator params="request: Request" />*/}
        <div className="pc-actions-links-header">
            <a className="pc-back-link" href={'#!pilotsettings?id=' + m.Model.Id() }>&lt;&lt; Back to Pilot Settings</a>
        </div>
        <h1 className="pc-page-title">
            <span className="pc-text-label-color">More</span><span className="pc-icon-bullet pc-text-label-color"></span>
            <span className="pc-text-label-color">Pilot Settings</span><span className="pc-icon-bullet"></span>
            <span>Manage Lookups</span>
        </h1>
        <PageBody model={m} />
    </div>
);

let PageBody = SuperCritical(({ model: m } : { model : ManagePilotLookups }) =>
    <div className="page-body pc-card">
        <AgencyCodesView model={m} />
        <PilotsView model={m} />
        <TugCompaniesView model={m} />
        <LineHandlersView model={m} />
    </div>
);

let AgencyCodesView = SuperCritical(({ model: m } : { model : ManagePilotLookups }) =>
    <div className="lookup-section agencycodes">
        <h2 className="pc-card-title lookup-section-title">Agencies</h2>
        <div className="lookup-description pc-text-smalllabel">
            This is a list of agent organizations and their agency codes.
            You can only add agencies that are registered on PortCall.com.
            Provide a code which will be seen on the Dispatch Dashboard.
        </div>
        <div className="lookup-section-content">
            <div className="lookup-section-headers">
                <span className="lookup-col-title name-col">Name</span>
                <span className="lookup-col-title code-col">Code</span>
            </div>
            <ul className="lookup-section-list">
                {m.AgencyCodes().map(c =>
                    <AgencyCodeView agencyCode={c} model={m} key={c.Model.CodedOrganizationId()} />
                )}
            </ul>
            <div className="add-btn-row">
                {m.EnableAddAgencyCode() &&
                    <a href="" onClick={noDefault(m.AddAgencyCode)}>+Add Agency</a>
                }
            </div>
        </div>
    </div>
);

let AgencyCodeView = SuperCritical(({ agencyCode: c, model: m } : { agencyCode : AgencyCodeViewModel, model : ManagePilotLookups }) =>
    <li>
        <div>
            <DeleteBtn item={c} />
            <span className="name-col pc-text-fixedwidth">
                {c.Editing() ?
                    <span>
                        <select className="cell-edit pc-select"
                            value={c.Model.CodedOrganizationId()}
                            onChange={e => c.Model.CodedOrganizationId(+e.currentTarget.value)}>
                            {m.OrganizationsAvailableForAgencyCode(c).map(o =>
                                <option value={o.Id()} key={o.Id()}>{o.Name()}</option>
                            )}
                        </select>
                        <span className="pc-select-chevron" />
                    </span> :
                    <span className="cell-view">
                        {c.Model.CodedOrganization() && c.Model.CodedOrganization()!.Name()}
                    </span>
                }
            </span>
            <span className="code-col">
                <TextEditControl value={c.Model.Code} editing={c.Editing} />
            </span>
            <EditToggle item={c} />
        </div>
        <div>
            <ValidationErrorView error={() => c.Validation()['Code']} />
        </div>
    </li>
);

let PilotsView = SuperCritical(({ model: m } : { model : ManagePilotLookups }) =>
    <div className="lookup-section pilots">
        <h2 className="pc-card-title lookup-section-title">Pilots</h2>
        <div className="lookup-description pc-text-smalllabel">
            This is a list of all the users with the Pilot permission.
            Provide each pilot with a code, and it will be reflected on the Dispatch Dashboard.
        </div>
        <div className="lookup-section-content">
            <div className="lookup-section-headers">
                <span className="lookup-col-title name-col">Name</span>
                <span className="lookup-col-title code-col">Code</span>
            </div>
            <ul className="lookup-section-list">
                {m.Pilots().map(p =>
                    <PilotView pilot={p} key={p.Model.Id()} />
                )}
            </ul>
        </div>
    </div>
);

let PilotView = SuperCritical(({ pilot: p } : { pilot : PilotViewModel }) =>
    <li>
        <div>
            <span className="name-col pc-text-fixedwidth">
                <span className="cell-view">{p.Model.User() && p.Model.User()!.FullName()}</span>
            </span>
            <span className="code-col">
                <TextEditControl value={p.Code} editing={p.Editing} />
            </span>
            <EditToggle item={p} />
        </div>
        <div>
            <ValidationErrorView error={() => p.Validation()['Code']} />
        </div>
    </li>
);

let TugCompaniesView = SuperCritical(({ model: m } : { model : ManagePilotLookups }) =>
    <div className="lookup-section">
        <h2 className="pc-card-title lookup-section-title">Tug Companies</h2>
        <div className="lookup-description pc-text-smalllabel">
            This is a list of tug companies that work within the port.
            These names will be listed on the Pilot Order form.
            Provide each tug company with a code, and it will be reflected on the Dispatch Dashboard.
        </div>
        <div className="lookup-section-content">
            <div className="lookup-section-headers">
                <span className="lookup-col-title name-col">Name</span>
                <span className="lookup-col-title code-col">Code</span>
            </div>
            <ul className="lookup-section-list">
                {m.TugCompanies().map(tug =>
                    <TugCompanyView tug={tug} key={tug.Model.Id()} />
                )}
            </ul>
            <div className="add-btn-row">
                {m.EnableAddTug() &&
                    <a href="" onClick={noDefault(m.AddTugCompany)}>+Add Tug Co.</a>
                }
            </div>
        </div>
    </div>
);

let TugCompanyView = SuperCritical(({ tug } : { tug : TugCompanyViewModel }) =>
    <li>
        <div>
            <DeleteBtn item={tug} />
            <span className="name-col pc-text-fixedwidth">
                <TextEditControl value={tug.Model.Name} editing={tug.Editing} />
            </span>
            <span className="code-col">
                <TextEditControl value={tug.Model.Code} editing={tug.Editing} />
            </span>
            <EditToggle item={tug} />
        </div>
        <div>
            <ValidationErrorView error={() => tug.Validation()['Name']} />
            <ValidationErrorView error={() => tug.Validation()['Code']} />
        </div>
    </li>
);

let LineHandlersView = SuperCritical(({ model: m } : { model : ManagePilotLookups }) =>
    <div className="lookup-section">
        <h2 className="pc-card-title lookup-section-title">Line Handlers</h2>
        <div className="lookup-description pc-text-smalllabel">
            This is a list of line handlers that work within the port.
            These names will be listed on the Pilot Order form and the Movement Details popup on the Dispatch Dashboard.
        </div>
        <div className="lookup-section-content">
            <div className="lookup-section-headers">
                <span className="lookup-col-title name-col">Name</span>
                <span className="lookup-col-title code-col">Code</span>
            </div>
            <ul className="lookup-section-list">
                {m.LineHandlingCompanies().map(l =>
                    <LineHandlerView lineHandler={l} key={l.Model.Id()} />
                )}
            </ul>
            <div className="add-btn-row">
                {m.EnableAddLineHandler() &&
                    <a href="" onClick={noDefault(m.AddLineHandlingCompany)}>+Add Line Handler</a>
                }
            </div>
        </div>
    </div>
);

let LineHandlerView = SuperCritical(({ lineHandler: l } : { lineHandler : LineHandlingCompanyViewModel }) =>
    <li>
        <div>
            <DeleteBtn item={l} />
            <span className="name-col pc-text-fixedwidth">
                <TextEditControl value={l.Model.Name} editing={l.Editing} />
            </span>
            {/* TH: 2/17/16 -- I'm including Code even though it isn't in the current designs.
                It seems useful in case we ever include line handling companies on the dispatch dashboard.
                Can easily hide the UI later. */}
            <span className="code-col">
                <TextEditControl value={l.Model.Code} editing={l.Editing} />
            </span>
            <EditToggle item={l} />
        </div>
        <div>
            <ValidationErrorView error={() => l.Validation()['Name']} />
            <ValidationErrorView error={() => l.Validation()['Code']} />
        </div>
    </li>
);

let DeleteBtn = SuperCritical(({ item } : { item : { Editing : () => boolean, Delete : () => void}}) =>
    <a href="" className={cx('delete-btn pc-close-icon', {
            'delete-btn-hide': !item.Editing()
        })}
        onClick={noDefault(item.Delete)}></a>
);

let EditToggle = SuperCritical(({ item } : { item : { Editing : DataSignal<boolean> }}) =>
    item.Editing() ?
        <a href="" className="edit-toggle edit-done" onClick={noDefault(() => item.Editing(false))}>Done</a> :
        <a href="" className="edit-toggle"           onClick={noDefault(() => item.Editing(true))}>Edit</a>
);

let TextEditControl = SuperCritical(({ value, editing } : { value : DataSignal<string | null>, editing : () => boolean }) =>
    editing() ?
        <input className="cell-edit"
            value={value() || ''}
            onChange={e => value(e.currentTarget.value)} /> :
        <span className="cell-view">{value()}</span>
);


// WEBPACK FOOTER //
// ./apps/pilots/managepilotlookups/view.tsx